import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import HouseStats from '../../components/common/HouseStats';
import PersonAvatar from '../../components/common/PersonAvatar';
import { apiErrorText, apiRequest, saveWithOptionalFile } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import { LockedCreateButton, useCanBuild } from '../../components/common/ModuleInterest';

/* The standing divisions the company sorts itself into, and who is in each.

   Written HERE and not on an event, because a house outlives the day: a person is of the red
   house on days they do not play and between two events. An event picks from this shelf on its
   own screen, and that is where teams are sorted and points are given.

   ★ A membership ENDS, it is never deleted — who played for Red last season is a fact. That is
   why the bin says "end" and the row quietly stops being listed.

   ★★ Putting three hundred people into four houses is a LIST-shaped job (filter, tick, assign,
   upload a spreadsheet) and this screen is shelf-shaped: it is where the four houses are invented
   and named. So the people live on the roster screen and this says how many of them are placed
   and links across. The one thing that does belong here is the random deal, because it is about
   the shelf as a whole and not about anybody in particular. */

// A colour is required by the store, so the draft opens with one rather than with an empty field
// that refuses on save. The picture is optional and stays optional: the colour alone already
// tells four houses apart, and a required upload would stop a company inventing its houses on the
// afternoon it decides to have them.
const EMPTY_HOUSE = { id: '', name: '', color: '#cc0000', image: '' };

/* What a house's people won on the competition days, added up over the members.

   ⚠️⚠️ ONLY under the cup, and the screen checks the basis before it draws this. The cup hands a
   team's points to the PEOPLE who played, so the members of a house add up to the house's cup
   total exactly. The classic figure does not work that way: a house scores what its teams score
   once, and every player of a team carries that same team total, so adding them up would report a
   house four times richer than it is. */
const wonIn = (house, points) => (house.members || [])
  .reduce((sum, m) => sum + (((points || {})[m.person_id] || {}).points || 0), 0);

/* And the other half: what these people earned for the house off the field.

   ★ This one always adds up, under either basis. The engagement weights are counted per PERSON
   and reach a house through that person's standing membership — the same road a cup share takes —
   so the members of a house are exactly the house's `earned` figure on the stats block below. */
const earnedIn = (house, points) => (house.members || [])
  .reduce((sum, m) => sum + (((points || {})[m.person_id] || {}).earned || 0), 0);

// One member's line out of that answer. `won` is null until the second read lands, which is why
// every reader goes through here rather than reaching into it.
const memberPoints = (member, won) => (((won || {}).people || {})[member.person_id] || {}).points
  || 0;

const memberEarned = (member, won) => (((won || {}).people || {})[member.person_id] || {}).earned
  || 0;

/* What the chip says when you rest on it — the sentence for the basis, plus the part with their
   own name on it when there is one.

   ⚠️ Under the classic figure the warning is not optional: these numbers do not add up to the
   house, and the one place to say so is where somebody is reading them. */
const pointsTitle = (t, member, won) => {
  const row = ((won || {}).people || {})[member.person_id] || {};
  const parts = [t(won && won.basis === 'cup' ? 'houseMemberCupTitle' : 'houseMemberTeamTitle')];
  if (row.own > 0) parts.push(t('houseMemberOwnPoints', { n: row.own }));
  return parts.join(' ');
};

const CompanyHouses = () => {
  const { t } = useTranslation();
  // The paid "Own events" module behind every build button here (null while it loads).
  const canBuild = useCanBuild();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(null);
  const [roster, setRoster] = useState(null);
  // What every member won on the competition days. Its own read, because it is the expensive one
  // — a day's ranking per converted day — and the shelf must not wait on it; the chips fill in
  // when it lands. Null while it is on its way, so nothing pretends the answer is "nothing".
  const [won, setWon] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // The random spread: whether it should also move people who already have a house, and what it
  // said last time. The second is kept apart from `notice` because it is an ANSWER to a button
  // and belongs beside it — "0 placed" read at the top of the screen looks like nothing happened.
  const [reshuffle, setReshuffle] = useState(false);
  const [spreadNotice, setSpreadNotice] = useState(null);
  // A counter the stats block watches, so a spread or a rename re-reads the numbers under it
  // rather than leaving yesterday's beside today's shelf.
  const [stamp, setStamp] = useState(0);
  // The public wall board's token: undefined while it loads, null while the board is off.
  const [boardToken, setBoardToken] = useState(undefined);
  const [boardCopied, setBoardCopied] = useState(false);
  // Said beside the board buttons, not in the shelf's notice at the top of the screen: an error
  // the admin cannot see from the button they just pressed reads as "the button does nothing".
  const [boardNotice, setBoardNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/company/houses')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Who the company's people are, to add them from. On mount rather than on a tab click — this is
  // its own screen now, so the roster loads with it.
  useEffect(() => {
    apiRequest('GET', '/company/houses/roster')
      .then((res) => setRoster(res.roster || []))
      .catch(() => setRoster([]));
  }, []);

  // ★ Beside the shelf, never before it: a company with a season of converted days pays a second
  // or two for these sums, and a roster that will not draw until they arrive is a roster that
  // reads as broken. A screen with no chips is a screen with one less thing on it.
  useEffect(() => {
    apiRequest('GET', '/company/houses/points')
      .then(setWon)
      .catch(() => setWon({ people: {}, basis: 'raw' }));
  }, [stamp]);

  useEffect(() => {
    apiRequest('GET', '/company/houses/board-link')
      .then((res) => setBoardToken(res.token || null))
      .catch(() => setBoardToken(null));
  }, []);

  const boardUrl = boardToken ? `${window.location.origin}/house-board/${boardToken}` : null;

  /* The big screen. 'on' keeps an existing address — a display already showing it keeps working —
     and only mints one the first time. The tab is opened BEFORE the request and pointed at the
     board after, because a window.open after an await is a popup the browser blocks. */
  const showBoard = () => {
    const tab = window.open('', '_blank');
    setBoardNotice(null);
    apiRequest('POST', '/company/houses/board-link', { action: 'on' })
      .then((res) => {
        setBoardToken(res.token || null);
        if (tab && res.token) tab.location.href = `/house-board/${res.token}`;
      })
      .catch((err) => { if (tab) tab.close(); setBoardNotice(apiErrorText(t, err)); });
  };

  const changeBoard = (action) => {
    if (!window.confirm(t(action === 'off' ? 'houseBoardOffConfirm' : 'houseBoardRotateConfirm'))) {
      return;
    }
    apiRequest('POST', '/company/houses/board-link', { action })
      .then((res) => { setBoardToken(res.token || null); setBoardCopied(false); })
      .catch((err) => setBoardNotice(apiErrorText(t, err)));
  };

  const copyBoard = () => {
    if (!boardUrl || !navigator.clipboard) return;
    navigator.clipboard.writeText(boardUrl).then(() => setBoardCopied(true)).catch(() => {});
  };

  /* Every one of these answers with the whole shelf, which is what the screen draws, so the merge
     is the same one-liner each time — no local list to keep in step. */
  const merge = (res) => {
    setData((d) => ({ ...d, houses: res.houses }));
    setStamp((n) => n + 1);
  };

  const save = () => {
    if (busy || !draft || !draft.name.trim()) return;
    setBusy(true);
    setNotice(null);
    saveWithOptionalFile('/company/houses', draft, file)
      .then((res) => { merge(res); setDraft(null); setFile(null); })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  /* Deal the roster over the houses at random, as evenly as the numbers allow.

     ★ It asks first, and the question is not the same question twice: topping up only places the
     people who are in no house yet, while the reshuffle moves everybody — including the ones an
     admin sorted by hand last week. Only the second needs a warning, so only the second gets a
     confirm, and the confirm says what it will and will not touch. */
  const spread = () => {
    if (busy) return;
    // eslint-disable-next-line no-alert
    if (reshuffle && !window.confirm(t('spreadHousesConfirm'))) return;
    setBusy(true);
    setNotice(null);
    setSpreadNotice(null);
    apiRequest('POST', '/company/houses/spread', { include_assigned: reshuffle })
      .then((res) => {
        merge(res);
        // Two numbers, because they are two different promises: colleagues are in their house
        // now, uploaded addresses are in theirs the moment they accept.
        const parts = [t('spreadHousesDone', { n: res.placed || 0 })];
        if (res.pending) parts.push(t('spreadHousesPending', { n: res.pending }));
        setSpreadNotice(parts.join(' '));
        // The coverage line above the button is served by the API, so it has to be re-read — it
        // is the one thing on this screen the answer does not carry.
        load();
      })
      .catch((err) => setSpreadNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const remove = (house) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteHouseConfirm', { name: house.name }))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('DELETE', `/company/houses/${house.id}`)
      .then(merge)
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const addMember = (house, partnerId) => {
    if (busy || !partnerId) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/houses/${house.id}/members`, { partner_id: partnerId })
      .then(merge)
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const endMember = (house, member) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('endMembershipConfirm', { name: member.name || '' }))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('DELETE', `/company/houses/${house.id}/member/${member.member_id}`)
      .then(merge)
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const houses = (data && data.houses) || [];
  // How much of the roster is in a house. Served rather than summed from the rows above: the
  // houses carry their member counts but nothing here knows how many people there are to place,
  // which is the only half of "6 of 57" that matters.
  const coverage = (data && data.house_people) || { total: 0, assigned: 0, pending: 0 };
  // How many houses are already being played with somewhere. It does not block the shuffle — a
  // day's teams point at a house through Ploeg.houseid and standing membership is not in that
  // chain — but it is the state in which an admin most wants to be told so out loud.
  const inPlay = houses.filter((h) => h.on_events > 0).length;

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="home-location-alt" theme="primary" className="me-2" />
          {t('ourHouses')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('ourHousesNote')}</p>
            {houses.length === 0 && !draft && <p className="ce-note">{t('noHousesYet')}</p>}

            {/* ★★ Who is in the houses — the number, and the two ways of changing it.

                The count includes colleagues who have not accepted their invite yet: their house
                waits on their row and lands when they join, so leaving them out would report a
                company that has just filed its whole roster as half done. */}
            {houses.length > 0 && (
              <div className="bravos-housefill">
                <p className="bravos-housefill__count">
                  {t('houseCoverage', { assigned: coverage.assigned, total: coverage.total })}
                  {coverage.pending > 0 && ` · ${t('houseCoveragePending',
                                                  { n: coverage.pending })}`}
                </p>
                <div className="bravos-form__row">
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={() => navigate('/company-members')}
                  >
                    <Icon name="users" /> {t('manageHouseMembers')}
                  </button>
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={spread}
                    disabled={busy}
                  >
                    <Icon name="shuffle" /> {busy ? t('pleaseWait') : t('spreadHouses')}
                  </button>
                </div>
                <label className="bravos-netbatch__all">
                  <input
                    type="checkbox"
                    checked={reshuffle}
                    onChange={(e) => setReshuffle(e.target.checked)}
                  />
                  <span>{t('spreadHousesReshuffle')}</span>
                </label>
                {/* ⚠️ Said BEFORE the click and not only in the confirm: an admin mid-season will
                    assume a reshuffle rewrites the scores, and the truthful answer — a house
                    scores what its TEAMS score, which is set per event — is the thing that makes
                    the button safe to press at all. */}
                <span className="bravos-netform__hint">
                  {reshuffle ? t('spreadHousesWarn') : t('spreadHousesHint')}
                </span>
                {inPlay > 0 && (
                  <span className="bravos-netform__hint bravos-netform__hint--warn">
                    {t('spreadHousesInPlay', { count: inPlay })}
                  </span>
                )}
                {spreadNotice && <p className="bravos-cal__notice">{spreadNotice}</p>}
              </div>
            )}

            {houses.map((h) => (
              <div key={h.id}>
                <div className="bravos-themerow">
                  {/* Inline rather than a class: the colour IS the house, and it is chosen per row
                      by the company. The picture sits ON that colour rather than replacing it — a
                      logo with transparency then still reads, and a house keeps the one identity
                      it has everywhere else.
                      ⚠️ backgroundColor + backgroundImage, never the `background` shorthand: the
                      shorthand wipes the sizing the stylesheet sets and the picture tiles. */}
                  <span
                    className="bravos-themerow__img"
                    style={{ backgroundColor: h.color || '#5a6472',
                             backgroundImage: h.image ? `url("${h.image}")` : undefined }}
                  />
                  <span className="bravos-themerow__body">
                    <span className="bravos-themerow__name">{h.name}</span>
                    <span className="bravos-themerow__when">
                      {t('houseMemberCount', { count: h.member_count })}
                      {h.on_events > 0 && ` · ${t('houseOnEvents', { count: h.on_events })}`}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="bravos-member__makeadmin"
                    onClick={() => setOpen(open === h.id ? null : h.id)}
                  >
                    {open === h.id ? t('close') : t('houseMembers')}
                  </button>
                  <button
                    type="button"
                    className="bravos-member__makeadmin"
                    onClick={() => { setDraft({ ...EMPTY_HOUSE, ...h }); setFile(null); }}
                  >
                    {t('edit')}
                  </button>
                  <button
                    type="button"
                    className="bravos-member__remove"
                    onClick={() => remove(h)}
                    disabled={busy}
                    title={t('delete')}
                  >
                    <Icon name="trash" />
                  </button>
                </div>

                {open === h.id && (
                  <div className="bravos-form">
                    {(h.members || []).length === 0 && (
                      <p className="ce-note">{t('noHouseMembersYet')}</p>
                    )}
                    {/* ★ What this house got THROUGH these people, said once above their names.

                        ⚠️ The won half only under the cup, where the chips below genuinely add up
                        to it; the earned half always, because the engagement weights are counted
                        per person and land on the house they belong to whatever else happened. */}
                    {won && (wonIn(h, won.people) > 0 || earnedIn(h, won.people) > 0) && (
                      <p className="bravos-q__hint mb-1">
                        {[won.basis === 'cup' && wonIn(h, won.people) > 0
                          ? t('houseMembersWonTotal', { n: wonIn(h, won.people) }) : null,
                        earnedIn(h, won.people) > 0
                          ? t('houseMembersEarnedTotal', { n: earnedIn(h, won.people) }) : null]
                          .filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {(h.members || []).map((m) => (
                      <div key={m.member_id} className="bravos-themerow">
                        {/* The face, because this list is read to recognise people — an admin
                            checking who is in red knows the faces long before the full names. */}
                        <PersonAvatar person={m} />
                        <span className="bravos-themerow__body">
                          <span className="bravos-themerow__name">{m.name || m.email}</span>
                          {m.since && (
                            <span className="bravos-themerow__when">
                              {t('houseMemberSince', { date: m.since })}
                            </span>
                          )}
                        </span>
                        {/* What they won on the competition days.

                            ⚠️ Only when there is something: a column of zeroes down a roster of
                            three hundred reads as "nobody here does anything", on a company that
                            has simply never played a day yet.

                            ⚠️ The word changes with the basis, because the number means two
                            different things. Under the cup it is THEIR share of what their teams
                            won by placing; without a cup it is what their TEAM scored, which
                            every one of their team-mates carries too. */}
                        {memberPoints(m, won) > 0 && (
                          <span
                            className="bravos-themerow__pts"
                            title={pointsTitle(t, m, won)}
                          >
                            {t(won && won.basis === 'cup' ? 'houseMemberCupPoints'
                              : 'houseMemberTeamPoints', { n: memberPoints(m, won) })}
                          </span>
                        )}
                        {/* The other half, and its own chip rather than one sum: they are two
                            currencies of the same standing — what somebody won by playing and
                            what they earned by taking part — and a single number would hide
                            which of the two moved when they ask why. */}
                        {memberEarned(m, won) > 0 && (
                          <span
                            className="bravos-themerow__pts bravos-themerow__pts--earned"
                            title={t('houseMemberEarnedTitle')}
                          >
                            {t('houseMemberEarnedPoints', { n: memberEarned(m, won) })}
                          </span>
                        )}
                        <button
                          type="button"
                          className="bravos-member__remove"
                          onClick={() => endMember(h, m)}
                          disabled={busy}
                          title={t('endMembership')}
                        >
                          <Icon name="trash" />
                        </button>
                      </div>
                    ))}
                    {/* Only people not already in THIS house — somebody in another house of the
                        company still shows, because adding them here is how they move. */}
                    <label className="bravos-form__label">
                      {t('addHouseMember')}
                      <select
                        value=""
                        disabled={busy || !roster}
                        onChange={(e) => addMember(h, e.target.value)}
                      >
                        <option value="">{roster ? t('choose') : t('pleaseWait')}</option>
                        {(roster || [])
                          .filter((p) => !(h.members || []).some((m) => m.person_id === p.id))
                          .map((p) => (
                            <option key={p.id} value={p.id}>{p.name || p.email}</option>
                          ))}
                      </select>
                    </label>
                  </div>
                )}
              </div>
            ))}

            {draft ? (
              <div className="bravos-form">
                <input
                  type="text"
                  value={draft.name}
                  maxLength={50}
                  placeholder={t('houseNamePlaceholder')}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <label className="bravos-form__label">
                  {t('houseColor')}
                  <input
                    type="color"
                    value={draft.color || '#cc0000'}
                    onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                  />
                </label>
                {/* A crest, a mascot, a logo — whatever the company calls its houses by. It is
                    drawn small everywhere it appears (the roster, the home strip), so a square
                    picture survives the trip and a wide banner does not; the hint says so. */}
                <label className="bravos-form__label">
                  {t('houseImage')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                  />
                </label>
                <span className="bravos-netform__hint">{t('houseImageHint')}</span>
                <div className="bravos-form__row">
                  <button
                    type="button"
                    className="bravos-submit"
                    onClick={save}
                    disabled={busy || !draft.name.trim()}
                  >
                    {busy ? t('pleaseWait') : t('save')}
                  </button>
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={() => { setDraft(null); setFile(null); }}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (canBuild === false ? (
              /* Seeing the houses is free, making them is the paid "Own events" module. */
              <LockedCreateButton label={t('addHouse')} />
            ) : (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => { setDraft({ ...EMPTY_HOUSE }); setFile(null); }}
              >
                <Icon name="plus" /> {t('addHouse')}
              </button>
            ))}
            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </>
        )}
      </Card>

      <Card>
        <Title my="1">{t('houseStatsTitle')}</Title>
        {/* The standing on a wall: a lobby display or a beamer, by a public address that needs no
            login. Only once there are houses to rank. */}
        {houses.length > 0 && (
          <div className="bravos-form mb-2">
            <div className="bravos-form__row">
              <button type="button" className="bravos-submit" onClick={showBoard}>
                <Icon name="expand" /> {t('houseBoardShow')}
              </button>
            </div>
            {boardNotice && <p className="bravos-cal__notice">{boardNotice}</p>}
            {boardUrl && (
              <>
                <label className="bravos-form__label">
                  {t('houseBoardLink')}
                  <input type="text" readOnly value={boardUrl} onFocus={(e) => e.target.select()} />
                </label>
                <span className="bravos-netform__hint">{t('houseBoardHint')}</span>
                <div className="bravos-form__row">
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={copyBoard}>
                    <Icon name="copy" /> {boardCopied ? t('copied') : t('copyLink')}
                  </button>
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => changeBoard('rotate')}>
                    <Icon name="refresh" /> {t('houseBoardRotate')}
                  </button>
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => changeBoard('off')}>
                    <Icon name="cross" /> {t('houseBoardOff')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {/* Remounted whenever the shelf changes, so the numbers under it are never the ones from
            before the spread that just moved two hundred people. */}
        <HouseStats key={stamp} />
      </Card>
    </Base>
  );
};

export default CompanyHouses;
